import type { LunchComponent } from '../domain/types.js';

const make = (
  id: string,
  name: string,
  type: LunchComponent['type'],
  ingredientId: string,
  qtyPerLunch: number,
  unit: LunchComponent['unit'],
): LunchComponent => ({ id, name, type, ingredientId, qtyPerLunch, unit });

/**
 * Built for 2.5-year-olds at nursery: small, cold, finger-food, no cutlery,
 * and no nuts of any kind. Quantities are per lunch box, not per portion —
 * a toddler's sandwich is still a whole sandwich.
 */
export const LUNCH_COMPONENTS: LunchComponent[] = [
  // Base — bread, wraps, containers
  // bread-white/bread-wholemeal are counted in SLICES (see ingredients.ts), so
  // 2 each here is already correct: one sandwich is two slices.
  make('sandwich-white', 'White bread sandwich', 'base', 'bread-white', 2, 'each'),
  make('sandwich-brown', 'Wholemeal sandwich', 'base', 'bread-wholemeal', 2, 'each'),
  make('wrap-base', 'Small wrap', 'base', 'wraps', 1, 'each'),
  make('pitta-fingers', 'Pitta fingers', 'base', 'pitta-bread', 1, 'each'),
  make('pasta-pot', 'Cold pasta pot', 'base', 'pasta-fusilli', 45, 'g'),

  // Filling — sandwich spreads and proteins
  make('fill-ham', 'Ham', 'filling', 'ham-slices', 25, 'g'),
  make('fill-cheddar', 'Cheddar', 'filling', 'cheddar', 20, 'g'),
  make('fill-cream-cheese', 'Cream cheese', 'filling', 'cream-cheese', 20, 'g'),
  make('fill-tuna-mayo', 'Tuna mayo', 'filling', 'tuna-tinned', 30, 'g'),
  make('fill-egg-mayo', 'Egg mayo', 'filling', 'eggs', 1, 'each'),
  make('fill-chicken', 'Chicken slices', 'filling', 'cooked-chicken-slices', 25, 'g'),
  make('fill-jam', 'Jam', 'filling', 'jam', 15, 'g'),
  make('fill-butter', 'Butter', 'filling', 'butter', 10, 'g'),

  // Side — vegetables and dips
  make('side-cucumber', 'Cucumber sticks', 'side', 'cucumber', 0.15, 'each'),
  make('side-carrot', 'Carrot sticks', 'side', 'carrots', 40, 'g'),
  make('side-tomatoes', 'Cherry tomatoes, quartered', 'side', 'fresh-tomatoes', 40, 'g'),
  make('side-pepper', 'Pepper strips', 'side', 'peppers', 0.25, 'each'),
  make('side-sweetcorn', 'Sweetcorn pot', 'side', 'sweetcorn-tinned', 40, 'g'),

  // Snack — finger food, dried fruit, treats
  make('snack-yoghurt', 'Yoghurt pot', 'snack', 'yoghurt-pots', 1, 'each'),
  make('snack-banana', 'Banana', 'snack', 'bananas', 0.5, 'each'),
  make('snack-apple', 'Apple slices', 'snack', 'apples', 0.5, 'each'),
  make('snack-grapes-quartered', 'Grapes, quartered', 'snack', 'grapes', 40, 'g'),
  make('snack-raisins', 'Raisins', 'snack', 'raisins', 20, 'g'),
  make('snack-crisps', 'Crisps', 'snack', 'crisps', 1, 'each'),
  make('snack-biscuit', 'Biscuit', 'snack', 'biscuits', 15, 'g'),
  make('snack-cereal-bar', 'Cereal bar', 'snack', 'cereal-bars', 1, 'each'),

  // Drink — cold beverages
  make('drink-milk', 'Milk', 'drink', 'milk', 200, 'ml'),
  make('drink-squash', 'Squash', 'drink', 'squash', 200, 'ml'),
];

/**
 * Index for O(1) lookup by id.
 */
export const LUNCH_COMPONENTS_BY_ID = new Map(
  LUNCH_COMPONENTS.map((c) => [c.id, c]),
) as ReadonlyMap<string, LunchComponent>;
